import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type Theme = 'light' | 'dark' | 'system'

export type ThemeContextType = {
  theme: Theme
  setTheme: (theme: Theme) => void
  isDark: boolean
  toggleTheme: () => void
}

const STORAGE_KEY = 'rfid-re-fe.theme'

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const raw = localStorage.getItem(STORAGE_KEY) as Theme | null
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system'
  })
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    const root = window.document.documentElement

    const applyTheme = () => {
      let effective: 'light' | 'dark'
      if (theme === 'system') {
        effective = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      } else {
        effective = theme
      }

      root.classList.toggle('dark', effective === 'dark')
      root.classList.toggle('light', effective === 'light')
      root.style.colorScheme = effective
      setIsDark(effective === 'dark')
      localStorage.setItem(STORAGE_KEY, theme)
    }

    applyTheme()

    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      const onChange = () => applyTheme()
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }
  }, [theme])

  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark')

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
